import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { inputCls, fmtMoney } from './shared';

const errMsg = (e) => e?.response?.data?.detail || 'Erreur';
const EMPTY = { client_name: '', client_phone: '', client_email: '', need: 'location', budget: '', notes: '' };

export const ReferralDialog = ({ open, onClose, active, property = null, onDone }) => {
  const [form, setForm] = useState(EMPTY);
  const [to, setTo] = useState('');
  const [direction, setDirection] = useState('sent');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setForm(EMPTY); setTo(property?.agency_id || ''); setDirection('sent'); }
  }, [open, property]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isExternal = !!active.find((p) => p.partner.id === to)?.partner?.external;

  const submit = async () => {
    if (!to) { toast.error('Choisissez une agence partenaire'); return; }
    if (!form.client_name.trim()) { toast.error('Nom du client requis'); return; }
    setBusy(true);
    try {
      await api.post('/partnerships/referrals', {
        ...form, to_agency_id: to, budget: form.budget ? Number(form.budget) : null,
        client_phone: form.client_phone || null, client_email: form.client_email || null, notes: form.notes || null,
        property_id: property?.id || null, direction: isExternal ? direction : 'sent',
      });
      toast.success('Client recommandé'); onClose(); onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="referral-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Recommander un client</DialogTitle>
          <DialogDescription>{property ? `Pour le bien « ${property.title} »` : "L'agence partenaire pourra le contacter."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 text-xs font-semibold text-gray-600">Agence partenaire
            <select value={to} onChange={(e) => setTo(e.target.value)} disabled={!!property} className={inputCls} data-testid="referral-agency-select">
              <option value="">— Choisir —</option>
              {active.map((p) => <option key={p.partner.id} value={p.partner.id}>{p.partner.name}{p.partner.external ? ' (hors plateforme)' : ''}</option>)}
            </select>
          </label>
          {isExternal && (
            <div className="col-span-2 grid grid-cols-2 gap-2" data-testid="referral-direction-toggle">
              {[['sent', 'Client envoyé à cette agence'], ['received', 'Client reçu de cette agence']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setDirection(v)}
                  className={`rounded-xl border-2 px-3 py-2 text-xs font-semibold transition-colors ${direction === v ? 'border-[#B85C38] bg-[#B85C38]/5 text-[#B85C38]' : 'border-gray-200 text-gray-600'}`}
                  data-testid={`referral-direction-${v}`}>{l}</button>
              ))}
            </div>
          )}
          <label className="col-span-2 text-xs font-semibold text-gray-600">Nom du client *
            <input value={form.client_name} onChange={set('client_name')} className={inputCls} data-testid="referral-name-input" />
          </label>
          <label className="text-xs font-semibold text-gray-600">Téléphone
            <input value={form.client_phone} onChange={set('client_phone')} className={inputCls} data-testid="referral-phone-input" />
          </label>
          <label className="text-xs font-semibold text-gray-600">E-mail
            <input value={form.client_email} onChange={set('client_email')} className={inputCls} data-testid="referral-email-input" />
          </label>
          <label className="text-xs font-semibold text-gray-600">Besoin
            <select value={form.need} onChange={set('need')} className={inputCls} data-testid="referral-need-select">
              <option value="location">Location</option><option value="achat">Achat</option>
              <option value="vente">Vente</option><option value="gestion">Gestion</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-gray-600">Budget (FCFA)
            <input type="number" min="0" value={form.budget} onChange={set('budget')} className={inputCls} data-testid="referral-budget-input" />
          </label>
          <label className="col-span-2 text-xs font-semibold text-gray-600">Notes
            <textarea rows={2} value={form.notes} onChange={set('notes')} className={inputCls} data-testid="referral-notes-input" />
          </label>
        </div>
        <Button onClick={submit} disabled={busy} className="w-full bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-xl" data-testid="referral-submit">
          {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Envoyer
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const ConcludeDialog = ({ referral, onClose, onDone }) => {
  const [amount, setAmount] = useState('');
  useEffect(() => { setAmount(''); }, [referral]);
  const split = referral?.commission_split ?? 50;
  const share = Math.round((Number(amount) || 0) * split / 100);

  const submit = async () => {
    if (amount === '' || Number(amount) < 0) { toast.error('Montant invalide'); return; }
    try {
      await api.put(`/partnerships/referrals/${referral.id}/status`, { status: 'conclu', commission_amount: Number(amount) });
      toast.success('Affaire conclue, commission partagée'); onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={!!referral} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="conclude-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#2B3A2C]">Affaire conclue</DialogTitle>
          <DialogDescription>{referral?.client_name} · recommandé par {referral?.from_agency_name}</DialogDescription>
        </DialogHeader>
        <label className="text-xs font-semibold text-gray-600">Commission encaissée (FCFA)
          <input type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} data-testid="conclude-amount-input" />
        </label>
        <div className="rounded-xl bg-[#F5EFE1] p-3 text-sm space-y-1" data-testid="conclude-split-preview">
          <p className="flex justify-between"><span>{referral?.from_agency_name} ({split}%)</span><b>{fmtMoney(share)}</b></p>
          <p className="flex justify-between"><span>{referral?.to_agency_name} ({100 - split}%)</span><b>{fmtMoney((Number(amount) || 0) - share)}</b></p>
        </div>
        <Button onClick={submit} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="conclude-submit">Valider</Button>
      </DialogContent>
    </Dialog>
  );
};
